/**
 * AIT file writer implementation.
 */

import {
  AITBundle,
  AITMetadata,
  Permission,
  ZipIndexEntry,
  CompressionMethod,
  PlatformType,
} from "@apps-in-toss/ait-format-proto";
import { FORMAT_VERSION, LENGTH_SIZE, MAGIC, VERSION_SIZE } from "./constants";
import { computeSha256Hex, generateUUID7, isValidUUID } from "./utils";

export interface AITWriterOptions {
  /** Application name (required) */
  appName: string;
  /** Unique identifier for this deployment. If not provided, a UUIDv7 will be auto-generated. */
  deploymentId?: string;
  /** AIT format version (default: current version) */
  formatVersion?: number;
  /** Creator info string (e.g., "my-service/1.0.0") */
  createdBy?: string;
}

export interface MetadataOptions {
  /** Whether this is a game app */
  isGame?: boolean;
  /** Platform type (WEB or REACT_NATIVE) */
  platform?: PlatformType;
  /** Runtime version string */
  runtimeVersion?: string;
  /** List of bundle file names */
  bundleFiles?: string[];
  /** Package JSON content */
  packageJson?: Record<string, unknown>;
  /** Additional metadata */
  extra?: Record<string, unknown>;
  /** SDK Version */
  sdkVersion?: string;
}

interface FileEntry {
  name: string;
  data: Uint8Array;
  compress: boolean;
}

/**
 * Writer for AIT (Apps-in-Toss) bundle files.
 *
 * @example
 * ```typescript
 * // With auto-generated UUIDv7
 * const writer = new AITWriter({ appName: "my-app" });
 * writer.setMetadata({ isGame: false, platform: PlatformType.WEB });
 * writer.addFile("index.html", new TextEncoder().encode("<html>...</html>"));
 * const buffer = await writer.toBuffer();
 *
 * // With explicit deployment ID
 * const writer = new AITWriter({
 *   deploymentId: "019bfa90-ad4c-799f-b227-b4159e6867f7",
 *   appName: "my-app"
 * });
 * ```
 */
export class AITWriter {
  private readonly formatVersion: number;
  private readonly deploymentId: string;
  private readonly appName: string;
  private readonly createdBy: string;
  private metadata: AITMetadata | undefined = undefined;
  private readonly permissions: Permission[] = [];
  private readonly files: FileEntry[] = [];
  private signature: Uint8Array | null = null;

  constructor(options: AITWriterOptions) {
    if (!options.appName) {
      throw new Error("appName must not be empty");
    }

    if (options.deploymentId === undefined) {
      // Auto-generate UUIDv7
      this.deploymentId = generateUUID7();
    } else {
      if (!options.deploymentId) {
        throw new Error("deploymentId must not be empty");
      }
      if (!isValidUUID(options.deploymentId)) {
        throw new Error(
          "deploymentId must be a valid UUID format (e.g., '019bfa90-ad4c-799f-b227-b4159e6867f7')"
        );
      }
      this.deploymentId = options.deploymentId;
    }

    this.formatVersion = options.formatVersion ?? FORMAT_VERSION;
    this.appName = options.appName;
    this.createdBy = options.createdBy ?? "";
  }

  /**
   * Set the bundle metadata.
   */
  setMetadata(options: MetadataOptions): this {
    this.metadata = {
      isGame: options.isGame ?? false,
      platform: options.platform ?? PlatformType.PLATFORM_UNSPECIFIED,
      runtimeVersion: options.runtimeVersion ?? "",
      bundleFiles: options.bundleFiles ?? [],
      packageJson: options.packageJson,
      sdkVersion: options.sdkVersion ?? '',
      extra: options.extra,
    };
    return this;
  }

  /**
   * Add a permission entry.
   */
  addPermission(name: string, access: string): this {
    this.permissions.push({ name, access });
    return this;
  }

  /**
   * Add a file to the bundle.
   */
  addFile(name: string, data: Uint8Array, options?: { compress?: boolean }): this {
    this.files.push({
      name,
      data,
      compress: options?.compress ?? true,
    });
    return this;
  }

  /**
   * Set the digital signature.
   */
  setSignature(signature: Uint8Array): this {
    this.signature = signature;
    return this;
  }

  /**
   * Build the ZIP blob and index entries.
   */
  private async buildZipBlob(): Promise<{ zipBytes: Uint8Array; index: ZipIndexEntry[] }> {
    const { zipSync } = await import("fflate");

    const zipInput: Record<string, Uint8Array> = {};
    for (const file of this.files) {
      zipInput[file.name] = file.data;
    }

    const zipBytes = zipSync(zipInput, { level: 6 });

    // Build index entries with SHA256 hashes
    const index: ZipIndexEntry[] = await Promise.all(
      this.files.map(async (file) => ({
        name: file.name,
        zipOffset: BigInt(0), // Simplified - actual offset would need ZIP parsing
        compressedSize: BigInt(0),
        uncompressedSize: BigInt(file.data.length),
        compMethod: file.compress ? CompressionMethod.DEFLATE : CompressionMethod.NONE,
        sha256Hex: await computeSha256Hex(file.data),
        attrs: undefined,
      }))
    );

    return { zipBytes, index };
  }

  /**
   * Build the bundle protobuf message.
   */
  private buildBundle(index: ZipIndexEntry[]): AITBundle {
    return {
      formatVersion: this.formatVersion,
      deploymentId: this.deploymentId,
      appName: this.appName,
      metadata: this.metadata,
      permissions: this.permissions,
      index,
      createdBy: this.createdBy,
      createdAtMs: BigInt(Date.now()),
    };
  }

  /**
   * Serialize the AIT bundle to a buffer.
   */
  async toBuffer(): Promise<Uint8Array> {
    const { zipBytes, index } = await this.buildZipBlob();
    const bundle = this.buildBundle(index);
    const bundleBytes = AITBundle.encode(bundle).finish();

    // Calculate total size
    const signatureBytes = this.signature ?? new Uint8Array(0);
    const totalSize =
      MAGIC.length +
      VERSION_SIZE +
      LENGTH_SIZE +
      bundleBytes.length +
      LENGTH_SIZE +
      zipBytes.length +
      LENGTH_SIZE +
      signatureBytes.length;

    const buffer = new Uint8Array(totalSize);
    const view = new DataView(buffer.buffer);
    let offset = 0;

    // Write MAGIC
    buffer.set(MAGIC, offset);
    offset += MAGIC.length;

    // Write FORMAT_VERSION (big-endian)
    view.setUint32(offset, this.formatVersion, false);
    offset += VERSION_SIZE;

    // Write BUNDLE_LEN (big-endian uint64)
    view.setBigUint64(offset, BigInt(bundleBytes.length), false);
    offset += LENGTH_SIZE;

    // Write BUNDLE_BYTES
    buffer.set(bundleBytes, offset);
    offset += bundleBytes.length;

    // Write ZIP_BLOB_LEN (big-endian uint64)
    view.setBigUint64(offset, BigInt(zipBytes.length), false);
    offset += LENGTH_SIZE;

    // Write ZIP_BLOB_BYTES
    buffer.set(zipBytes, offset);
    offset += zipBytes.length;

    // Write SIGNATURE_LEN (big-endian uint64)
    view.setBigUint64(offset, BigInt(signatureBytes.length), false);
    offset += LENGTH_SIZE;

    // Write SIGNATURE_BYTES
    if (signatureBytes.length > 0) {
      buffer.set(signatureBytes, offset);
    }

    return buffer;
  }
}
